/** Espresso machine (Gaggiuino) — REST calls and shapes. Data arrives server-side over MQTT. */
import { buildQuery, request } from '../api.js';

/** [tMs, pressure, pumpFlow, weightFlow, temp, weight, waterPumped, targetTemp, targetFlow, targetPressure] */
export type ShotSample = [number, number, number, number, number, number, number, number, number, number];

export interface Shot {
  id: number;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  mode: string | null;
  profileId: number | null;
  profileName: string | null;
  doseG: number | null;
  yieldG: number | null;
  waterPumpedMl: number | null;
  peakPressure: number | null;
  avgPressure: number | null;
  avgTemp: number | null;
  targetTemp: number | null;
  caffeineMg: number | null;
  mine: boolean;
  source: string;
  beanId: number | null;
  grind: string | null;
  taste: string[];
  rating: number | null;
  tasteNotes: string | null;
}

export interface ShotDetail extends Shot {
  samples: ShotSample[];
}

export interface EspressoStatus {
  configured: boolean;
  state: Record<string, { payload: unknown; updatedAt: string }>;
  events: { at: string; kind: string; level: string | null; message: string }[];
  shotsToday: number;
  /** Of today's shots, the ones he drank. */
  mineToday: number;
  caffeineTodayMg: number;
}

export function getEspressoStatus(): Promise<EspressoStatus> {
  return request('/espresso/status');
}
export async function listShots(q: { limit?: number; before?: string; bean?: number } = {}): Promise<Shot[]> {
  return (await request<{ items: Shot[] }>(`/espresso/shots${buildQuery(q)}`)).items;
}
export async function getShot(id: string | number): Promise<ShotDetail> {
  return (await request<{ shot: ShotDetail }>(`/espresso/shots/${encodeURIComponent(String(id))}`)).shot;
}
export async function setShotMine(id: number, mine: boolean): Promise<Shot> {
  return updateShot(id, { mine });
}
export async function updateShot(
  id: number,
  patch: Partial<Pick<Shot, 'mine' | 'beanId' | 'grind' | 'taste' | 'rating' | 'tasteNotes'>>,
): Promise<Shot> {
  return (await request<{ shot: Shot }>(`/espresso/shots/${id}`, { method: 'PUT', body: JSON.stringify(patch) })).shot;
}

export interface Bean {
  id: number;
  name: string;
  roaster: string | null;
  origin: string | null;
  roastLevel: string | null;
  /** YYYY-MM-DD; null = unknown. */
  roastDate: string | null;
  bagNotes: string | null;
  notes: string | null;
  active: boolean;
  shots: number;
  lastShotAt: string | null;
}
export type BeanInput = Partial<Omit<Bean, 'id' | 'shots' | 'lastShotAt'>>;

export async function listBeans(): Promise<Bean[]> {
  return (await request<{ items: Bean[] }>('/espresso/beans')).items;
}
export async function saveBean(id: number | null, input: BeanInput): Promise<Bean> {
  const path = id === null ? '/espresso/beans' : `/espresso/beans/${id}`;
  return (await request<{ bean: Bean }>(path, { method: id === null ? 'POST' : 'PUT', body: JSON.stringify(input) })).bean;
}

export const ROAST_LEVELS = ['light', 'medium-light', 'medium', 'medium-dark', 'dark'] as const;

/** Whole days from roast to `today` (both YYYY-MM-DD); null when the roast date is unknown. */
export function daysOffRoast(roastDate: string | null, today: string): number | null {
  if (!roastDate) return null;
  return Math.round((Date.parse(today) - Date.parse(roastDate)) / 86_400_000);
}

/** Where a taste points: under-extracted (grind finer), over (coarser), uneven (puck prep), or fine. */
export type Pointer = 'under' | 'over' | 'uneven' | 'good';

export interface Taste {
  key: string;
  label: string;
  /** How to recognise it — the hard part is telling sour from bitter. */
  feels: string;
  points: Pointer;
}

/** The taste vocabulary for a shot. Keys are stored, so never rename one. */
export const TASTES: Taste[] = [
  { key: 'sour', label: 'Sour', feels: 'Sharp and tangy, hits fast on the sides of the tongue, makes your mouth water, then fades. Sugar barely helps.', points: 'under' },
  { key: 'bitter', label: 'Bitter', feels: 'Builds slowly and lingers at the back of the tongue, like burnt toast or very dark chocolate. Sugar softens it.', points: 'over' },
  { key: 'dry', label: 'Dry / chalky', feels: 'Astringent — the mouth feels stripped, like strong black tea or an unripe banana.', points: 'uneven' },
  { key: 'throat', label: 'Throat hit', feels: 'A harsh catch at the back of the throat on the way down.', points: 'under' },
  { key: 'thin', label: 'Thin / watery', feels: 'No body; slides off the tongue.', points: 'under' },
  { key: 'salty', label: 'Salty', feels: 'A savoury, brothy edge — the first thing to come out of the puck.', points: 'under' },
  { key: 'hollow', label: 'Hollow / flat', feels: 'Strong but with nothing in the middle; no sweetness, no fruit.', points: 'over' },
  { key: 'sweet', label: 'Sweet', feels: 'Caramel, sugar, ripe fruit — sweetness without any added.', points: 'good' },
  { key: 'balanced', label: 'Balanced', feels: 'Nothing sticks out; acidity, sweetness and bitterness in proportion.', points: 'good' },
  { key: 'syrupy', label: 'Syrupy', feels: 'Thick, heavy body that coats the mouth.', points: 'good' },
];

export const tasteFor = (key: string): Taste | undefined => TASTES.find((t) => t.key === key);

const POINTER_ADVICE: Record<Exclude<Pointer, 'good'>, string> = {
  under: 'Under-extracted: the water didn’t pull enough out. Grind finer (the main lever), or run a longer ratio, or brew 1–2 °C hotter.',
  over: 'Over-extracted: too much came out. Grind coarser (the main lever), or stop the shot sooner (shorter ratio), or brew 1–2 °C cooler.',
  uneven: 'Uneven extraction (channeling): some of the puck over-extracted while the rest under-extracted. Fix the puck before the grind — break up clumps (WDT), level, tamp flat.',
};

/** Brew shots only: how fast this one ran, judged on time to a normal 1:2–1:3 ratio. */
export function pace(s: Pick<Shot, 'durationMs' | 'yieldG' | 'doseG'>): 'fast' | 'normal' | 'slow' | null {
  if (s.yieldG === null || s.yieldG < 10) return null;
  const sec = s.durationMs / 1000;
  return sec < 22 ? 'fast' : sec > 36 ? 'slow' : 'normal';
}

/** What a shot's tastes (and its pace) say to change next. Empty when nothing was tagged. */
export function adviceFor(s: Pick<Shot, 'taste' | 'durationMs' | 'yieldG' | 'doseG'>): string[] {
  const points = new Set(s.taste.map((k) => tasteFor(k)?.points).filter((p): p is Pointer => !!p));
  if (points.size === 0) return [];
  const out: string[] = [];
  const p = pace(s);
  const sec = Math.round(s.durationMs / 1000);
  if (points.has('under') && points.has('over')) {
    out.push('Sour and bitter together usually means uneven extraction, not a grind that’s simply wrong — part of the puck ran fast while the rest stalled.');
    out.push(POINTER_ADVICE.uneven);
    if (p === 'slow') out.push(`It also ran slow (${sec} s), so a slightly coarser grind is still the right next step.`);
    if (p === 'fast') out.push(`It also ran fast (${sec} s), so a slightly finer grind is still the right next step.`);
    return out;
  }
  for (const k of ['under', 'over', 'uneven'] as const) if (points.has(k)) out.push(POINTER_ADVICE[k]);
  if (points.has('under') && p === 'slow')
    out.push(`But it ran slow (${sec} s) — a slow shot that tastes sour is usually channeling or stale beans, not a grind that’s too coarse. Check puck prep before grinding finer.`);
  if (points.has('over') && p === 'fast')
    out.push(`But it ran fast (${sec} s) — a fast shot that tastes bitter points to a very dark roast or water too hot, not the grind.`);
  if (out.length === 0) out.push('Tastes good — keep this grind and note it.');
  return out;
}

/** 'BREW_AUTO' → 'Brew'. */
export const MODE_LABEL: Record<string, string> = {
  BREW_AUTO: 'Brew',
  BREW_MANUAL: 'Manual brew',
  FLUSH: 'Flush',
  FLUSH_AUTO: 'Flush',
  DESCALE: 'Descale',
  STEAM: 'Steam',
  HOT_WATER: 'Hot water',
  HOME: 'Idle',
  PUMP_CALIBRATION: 'Pump calibration',
  POWER_OFF: 'Off',
};

export interface ProfileGuide {
  name: string;
  /** Lower-case fragments that identify this profile in the machine's own name for it. */
  match: string[];
  how: string;
  bestFor: string;
  expect: string;
  source: 'stock' | 'community';
}

/** A hand-written reference for what each espresso profile is for — shown on the Coffee page. */
export const PROFILE_GUIDE: ProfileGuide[] = [
  {
    name: 'Londinium',
    match: ['londinium'],
    how: 'Fill, ~10 s soak, 9 bar, then pressure eases off like a lever machine.',
    bestFor: 'Medium–dark roasts, milk drinks, everyday shots. The default.',
    expect: 'Sweet, thick, chocolatey, low bitterness. Hard to mess up.',
    source: 'stock',
  },
  {
    name: 'IUIUIU Classic',
    match: ['iuiuiu', 'classic'],
    how: 'Short pre-infusion, then flat 9 bar.',
    bestFor: 'Medium roasts, classic espresso.',
    expect: 'Punchy and balanced, like a café shot. Less forgiving of channeling.',
    source: 'stock',
  },
  {
    name: 'Adaptive',
    match: ['adaptive'],
    how: 'Adjusts flow to how the puck behaves.',
    bestFor: 'A new bag, dialing in. Dark-roast variants tame harshness.',
    expect: 'Steady results while the grind is still off.',
    source: 'stock',
  },
  {
    name: 'Blooming espresso',
    match: ['bloom'],
    how: 'Fill, long soak (20–30 s), extraction at falling pressure.',
    bestFor: 'Light, fruity single origins, drunk black.',
    expect: 'Bright, clear, juicy. 40 s+ total, often 1:2.5 or longer.',
    source: 'stock',
  },
  {
    name: 'Filter 2.1',
    match: ['filter'],
    how: 'Low pressure, high flow, long ratio.',
    bestFor: 'Very light roasts.',
    expect: 'Tea-like and fruity, thin body — close to strong filter coffee.',
    source: 'stock',
  },
  {
    name: 'Turbo shot',
    match: ['turbo'],
    how: 'Coarser grind, ~6 bar, fast (~15 s), 1:2.5–3.',
    bestFor: 'Medium–light roasts; when shots keep running fast.',
    expect: 'Sweet and clear, rarely bitter. Very forgiving of grind.',
    source: 'community',
  },
  {
    name: 'Extractamundo Dos!',
    match: ['extractamundo'],
    how: 'Lever-style pressure decline with a flow limit (ported from Decent).',
    bestFor: 'Medium roasts.',
    expect: 'Like Londinium, a little brighter.',
    source: 'community',
  },
  {
    name: 'Allongé',
    match: ['allong'],
    how: 'Coarse grind, high flow, 1:4–5 ratio.',
    bestFor: 'Very light roasts.',
    expect: 'Long and tea-like, a step toward filter coffee.',
    source: 'community',
  },
];

/** The guide entry for a profile name as the machine reports it, if any. */
export function guideFor(profileName: string | null | undefined): ProfileGuide | undefined {
  const n = (profileName ?? '').toLowerCase();
  return n ? PROFILE_GUIDE.find((g) => g.match.some((m) => n.includes(m))) : undefined;
}

export type ShotSort = 'newest' | 'oldest' | 'longest' | 'rated';
export interface ShotFilter {
  bean?: number | null;
  profile?: string | null;
  /** true = only tasted shots, false = only untasted. */
  tasted?: boolean | null;
}

export const isTasted = (s: Pick<Shot, 'rating' | 'taste' | 'tasteNotes'>): boolean =>
  s.rating !== null || s.taste.length > 0 || !!s.tasteNotes;

export function filterShots<S extends Shot>(shots: S[], f: ShotFilter): S[] {
  return shots.filter(
    (s) =>
      (f.bean == null || s.beanId === f.bean) &&
      (f.profile == null || s.profileName === f.profile) &&
      (f.tasted == null || isTasted(s) === f.tasted),
  );
}

export function sortShots<S extends Shot>(shots: S[], by: ShotSort): S[] {
  const t = (s: Shot): number => Date.parse(s.startedAt);
  const cmp: Record<ShotSort, (a: Shot, b: Shot) => number> = {
    newest: (a, b) => t(b) - t(a),
    oldest: (a, b) => t(a) - t(b),
    longest: (a, b) => b.durationMs - a.durationMs || t(b) - t(a),
    // Unrated last; ties newest first.
    rated: (a, b) => (b.rating ?? 0) - (a.rating ?? 0) || t(b) - t(a),
  };
  return [...shots].sort(cmp[by]);
}

/**
 * Consecutive runs of shots on the same day ('YYYY-MM-DD' from `dayOf`), in the
 * order given — so a time sort gets one section per day. Other sorts aren't
 * grouped (the caller shows one flat list).
 */
export function groupByDay<S extends Shot>(shots: S[], dayOf: (iso: string) => string): { day: string; shots: S[] }[] {
  const out: { day: string; shots: S[] }[] = [];
  for (const s of shots) {
    const day = dayOf(s.startedAt);
    const last = out.at(-1);
    if (last?.day === day) last.shots.push(s);
    else out.push({ day, shots: [s] });
  }
  return out;
}

/** Machine alerts worth a red line: faults, not the routine "Dropping beats in: 3" chatter. */
export const isFault = (e: { level: string | null; message: string }): boolean =>
  e.level === 'ERROR' || /fault|error|fail|runaway/i.test(e.message);
